import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import RhfCheckboxField from 'src/components/ReactHookForm/rhfCheckBoxField';
import RhfPasswordField from 'src/components/ReactHookForm/rhfPasswordField';
import RhfTextField from 'src/components/ReactHookForm/rhfTextField';
import useAuth from 'src/hooks/useAuth';
import { urls } from 'src/urls';
import { setFormErrors } from 'src/utils/form.util';
import * as yup from 'yup';

type LoginForm = {
  email: string;
  password: string;
  terms: boolean;
};

const getInitialValues = () => {
  return {
    email: '',
    password: '',
    terms: true
  };
};

const LoginJWT: FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth() as any;
  const { enqueueSnackbar } = useSnackbar();

  const loginSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('The email provided should be a valid email address'))
      .max(255)
      .required(t('Email is required')),
    password: yup.string().max(255).required(t('Password is required')),
    terms: yup
      .boolean()
      .oneOf([true], t('You must agree to our terms and conditions'))
  });

  const methods = useForm<LoginForm>({
    defaultValues: getInitialValues(),
    resolver: yupResolver(loginSchema)
  });

  const formSubmitHandler: SubmitHandler<LoginForm> = async (
    data: LoginForm
  ) => {
    try {
      const { email, password } = data;
      await login(email, password);
    } catch (err) {
      const { message } = err;
      let { errors } = err;

      if (!errors) {
        errors = { email: t(message), password: t(message) };
      }

      setFormErrors(methods.setError, errors);

      enqueueSnackbar(t(message), {
        variant: 'error'
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(formSubmitHandler)}>
        <Box>
          <Grid container>
            <Grid item xs={12} mb={2}>
              <RhfTextField label={t('Email')} name="email" />
            </Grid>
            <Grid item xs={12} mb={2}>
              <RhfPasswordField label={t('Password')} name="password" />
            </Grid>
            <Grid item xs={12} mb={2}>
              <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
              >
                <RhfCheckboxField
                  name="terms"
                  control={methods.control}
                  defaultChecked={true}
                  label={
                    <>
                      <Typography variant="body2">
                        <Link href={urls.termsOfService} underline="hover">
                          {t('I accept the terms and conditions')}
                        </Link>
                      </Typography>
                    </>
                  }
                />
                <Link
                  component={RouterLink}
                  underline="hover"
                  to="/account/recover-password"
                >
                  <b>{t('Lost password?')}</b>
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Button
          fullWidth
          size="large"
          variant="contained"
          type="submit"
          startIcon={
            methods.formState.isSubmitting ? (
              <CircularProgress size="1rem" />
            ) : null
          }
          disabled={methods.formState.isSubmitting}
          color="primary"
        >
          {t('Sign in')}
        </Button>
      </form>
    </FormProvider>
  );
};

export default LoginJWT;
